import { useLayoutEffect, type ReactNode } from "react";
import { pageDesignAttributes, pageDesignStyle } from "../../../shared/src/page-design-runtime";
import { walkPageNodes } from "../../../shared/src/page-tree";
import type { DocumentPageData } from "../server/page";
import type { SiteShell } from "../server/shell";
import { PageNodes } from "./website-page-nodes";

type PageData = Extract<DocumentPageData, { kind: "page" }>;

/**
 * An authored page's content, or the members-only gate when this visitor can't open it
 *. The gate shows the page's title and description only.
 */
export function DocumentContent({
  data,
  shell,
  builtIn,
  canvas = false,
}: {
  data: PageData;
  shell: SiteShell;
  /** The feature's own content, for a feature page's built-in block. */
  builtIn?: ReactNode;
  /** The editor's canvas, which shows where missing pictures and items go. */
  canvas?: boolean;
}) {
  const { document, bindings } = data;
  if (!document || !bindings)
    return (
      <div className="drate-page-root">
        <DocumentGate page={data.page} />
      </div>
    );
  return (
    <div
      className="drate-page-root"
      {...pageDesignAttributes(document.design)}
      style={pageDesignStyle(document.design)}
      aria-label={`${document.title} page content`}
    >
      <PageNodes
        path={data.page.path}
        creatorId={shell.creator.id}
        nodes={document.nodes}
        bindings={bindings}
        experiences={shell.experiences}
        preview={data.preview}
        canvas={canvas}
        creator={data.creator}
        member={data.member}
        textStyles={data.textStyles}
        elementStyles={data.elementStyles}
        priorityImageId={data.priorityImageId}
        openingSectionIds={data.openingSectionIds}
        images={data.images}
        builtIn={builtIn}
        owner={canvas ? undefined : (shell.owner ?? undefined)}
        // A guide template's elements bind to the guide its address names.
        item={bindings.record ?? undefined}
      />
      {walkPageNodes(document).some((node) => node.motion) && <PageMotion pageId={data.page.id} />}
    </div>
  );
}

function DocumentGate({ page }: { page: PageData["page"] }) {
  return (
    <section className="section">
      <div className="container document-gate">
        <p className="eyebrow">Members</p>
        <h1>{page.title}</h1>
        {page.description && <p className="prose">{page.description}</p>}
        <p>This page is available to members. Sign in to view it or choose a membership.</p>
        <div className="document-gate-actions">
          <a className="button button-primary" href="/join">
            View memberships <span aria-hidden="true">→</span>
          </a>
          <a
            className="button button-outline"
            href={`/sign-in?returnTo=${encodeURIComponent(page.path)}`}
          >
            Sign in
          </a>
        </div>
      </div>
    </section>
  );
}

/**
 * Animations on an authored page (shared/src/page-motion.ts), run with Motion.
 * Entrances start hidden only once this page is on screen, and a timer reveals everything if
 * Motion never starts, so a page never stays blank. Entrances in one section play together when
 * its first animated element scrolls into view; hover and press run forward and back. Reduced
 * motion keeps the fade of an entrance and drops movement and interaction effects.
 */
function PageMotion({ pageId }: { pageId: string }) {
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.dataset.motion = "ready";
    const reveal = setTimeout(() => {
      if (root.dataset.motion === "ready") delete root.dataset.motion;
    }, 2500);
    let stop: (() => void) | undefined;
    let cancelled = false;
    void import("../lib/page-motion").then(({ startPageMotion }) => {
      if (!cancelled) stop = startPageMotion();
    });
    return () => {
      cancelled = true;
      clearTimeout(reveal);
      stop?.();
      delete root.dataset.motion;
    };
  }, [pageId]);
  // A document's entrances are hidden before it first paints; a client navigation hides them in
  // the layout effect above, before the new page paints.
  return (
    <script
      dangerouslySetInnerHTML={{
        __html:
          "(()=>{const r=document.documentElement;r.dataset.motion='ready';setTimeout(()=>{if(r.dataset.motion==='ready')delete r.dataset.motion},2500)})()",
      }}
    />
  );
}
